import {
    Breadcrumb,
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { ClipboardCheck, FileText, LayoutGrid, Loader2, UserPlus } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext, useRefresh } from 'ra-core';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { ActivityTimeline } from '../activities/ActivityTimeline';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { ensureOpportunityForEnquiry } from './ensureOpportunity';

const isImageSrc = (src: string) => /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(src) || src.startsWith('data:image');
const isPdfSrc = (src: string) => /\.pdf(\?|$)/i.test(src) || src.startsWith('data:application/pdf');

/** Attachment chips that open an in-app preview (image/PDF) instead of just
 *  downloading. See bug tracker Enquiry #22. */
const EnquiryAttachments = ({ attachments }: { attachments: any[] }) => {
    const [preview, setPreview] = useState<any | null>(null);
    return (
        <Card className="overflow-hidden border-border dark:border-slate-800 rounded-2xl shadow-sm">
            <CardHeader className="pb-3 border-b bg-muted/50 dark:bg-slate-900/30">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    Attachments
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 bg-card dark:bg-slate-950/10">
                <div className="flex flex-wrap gap-3">
                    {attachments.map((att: any, i: number) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setPreview(att)}
                            className="flex items-center gap-2 px-3 py-2 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl text-sm font-medium text-primary hover:underline shadow-sm cursor-pointer"
                        >
                            <FileText className="w-4 h-4" />
                            {att.title || `Attachment ${i + 1}`}
                        </button>
                    ))}
                </div>
            </CardContent>

            <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
                <DialogContent className="max-w-6xl w-[90vw]">
                    <DialogHeader>
                        <DialogTitle className="truncate">{preview?.title || 'Attachment'}</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[80vh] overflow-auto rounded-md bg-muted">
                        {preview && isImageSrc(preview.src) && (
                            <img src={preview.src} alt={preview.title || 'attachment'} className="w-full h-auto rounded-md" />
                        )}
                        {preview && isPdfSrc(preview.src) && (
                            <iframe src={preview.src} title="attachment-preview" className="w-full h-[80vh] border-0" />
                        )}
                        {preview && !isImageSrc(preview.src) && !isPdfSrc(preview.src) && (
                            <p className="text-sm text-muted-foreground py-12 text-center">
                                Preview not available for this file type. Use the button below to open it.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        {preview && (
                            <a
                                href={preview.src}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90"
                            >
                                Open / Download
                            </a>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

const AssignedUserName = () => {
    const record = useRecordContext();
    if (!record) return <span>Unassigned</span>;
    return <span>{`${record.first_name || ''} ${record.last_name || ''}`.trim() || 'Unassigned'}</span>;
};

const EnquiryShowActions = () => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    
    const [open, setOpen] = useState(false);
    const [projectLoading, setProjectLoading] = useState(false);
    
    const [disqualifyOpen, setDisqualifyOpen] = useState(false);
    const [lostReason, setLostReason] = useState('');
    const [disqualifyLoading, setDisqualifyLoading] = useState(false);

    const handleQualify = async () => {
        if (!record) return;
        try {
            await dataProvider.update('enquiries', {
                id: record.id,
                data: { status: 'qualified', qualified_at: new Date().toISOString() },
                previousData: record
            });
            notify('Enquiry marked as Qualified', { type: 'success' });

            const created = await ensureOpportunityForEnquiry(dataProvider, record);
            if (created) notify('Opportunity created successfully', { type: 'success' });
            refresh();
        } catch (error: any) {
            notify(`Error qualifying enquiry: ${error.message}`, { type: 'error' });
        }
    };

    const handleDisqualify = async () => {
        if (!record || !lostReason) return;
        setDisqualifyLoading(true);
        try {
            await dataProvider.update('enquiries', {
                id: record.id,
                data: { status: 'disqualified', lost_reason: lostReason },
                previousData: record
            });
            notify('Enquiry marked as Disqualified', { type: 'success' });
            setDisqualifyOpen(false);
        } catch {
            notify('Error disqualifying enquiry', { type: 'error' });
        } finally {
            setDisqualifyLoading(false);
        }
    };

    const handleCreateQuotation = () => {
        if (!record) return;

        // E-2 / workflow linkage: carry the captured enquiry requirement into the
        // quotation as a pre-filled line item so sales doesn't re-key it.
        const req = record.requirements || {};
        const sizeParts = [req.length, req.width, req.height].filter(Boolean);
        const descParts = [
            req.product_type,
            req.dimensions ? `Variant: ${req.dimensions}` : null,
            sizeParts.length ? `Size: ${sizeParts.join(' x ')} mm` : null,
            req.materials ? `Material: ${req.materials}` : null,
            req.special_notes,
        ].filter(Boolean);

        // Always seed at least one line item from the enquiry so the quotation's
        // Line Items section is never empty (GRN/QTN #35). Fall back through the
        // available requirement fields, then the enquiry reference, for a usable
        // description — sales can refine price/qty afterwards.
        const description =
            descParts.join(' — ') ||
            req.product_type ||
            req.special_notes ||
            `Requirement from Enquiry ${record.reference}`;

        const items = [{
            description,
            hsn_code: '',
            quantity: Number(req.quantity) || 1,
            unit: 'Nos',
            unit_price: 0,
            tax_rate: 18,
            line_total: 0,
        }];

        navigate('/quotations/create', {
            state: {
                record: {
                    company_id: record.company_id,
                    customer_id: record.contact_id,
                    project_id: record.project_id,
                    items,
                    // Use a note to link back to the enquiry reference
                    notes: `Based on Enquiry: ${record.reference}`
                }
            }
        });
    };

    const handleConfirmProject = async () => {
        if (!record) return;
        setProjectLoading(true);
        try {
            await dataProvider.update('enquiries', {
                id: record.id,
                data: {
                    status: 'converted',
                    converted_to_project_at: new Date().toISOString()
                },
                previousData: record
            });
        } catch {
            // Non-critical — proceed anyway
        } finally {
            setProjectLoading(false);
        }
        setOpen(false);
        navigate('/projects/create', {
            state: {
                record: {
                    name: `Project for ${record.reference}`,
                    enquiry_id: record.id,
                    company_id: record.company_id,
                    contact_id: record.contact_id,
                    project_type: 'new_order',
                    estimated_value: record.requirements?.estimated_budget || record.requirements?.budget_min || undefined,
                    description: `Converted from Enquiry ${record.reference}`,
                    start_date: new Date().toISOString().split('T')[0],
                }
            }
        });
    };

    return (
        <div className="flex items-center gap-2">
            <EditButton />
            
            {(record?.status === 'new' || record?.status === 'in_discussion' || record?.status === 'qualified') && (
                <>
                    {record.status !== 'qualified' && (
                        <Button variant="outline" size="sm" onClick={handleQualify} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                            <ClipboardCheck className="w-4 h-4 mr-2" />
                            Mark Qualified
                        </Button>
                    )}

                    <Dialog open={disqualifyOpen} onOpenChange={setDisqualifyOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                                <LayoutGrid className="w-4 h-4 mr-2 rotate-45" />
                                Mark Lost
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Disqualify Enquiry</DialogTitle>
                                <DialogDescription>
                                    Please provide a reason why this enquiry is being disqualified.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="py-4">
                                <textarea
                                    className="w-full h-32 p-3 border rounded-md"
                                    placeholder="Enter reason for disqualification..."
                                    value={lostReason}
                                    onChange={(e) => setLostReason(e.target.value)}
                                />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setDisqualifyOpen(false)}>Cancel</Button>
                                <Button 
                                    variant="destructive" 
                                    onClick={handleDisqualify} 
                                    disabled={disqualifyLoading || !lostReason}
                                >
                                    {disqualifyLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Confirm Disqualify
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </>
            )}

            <Button variant="outline" size="sm" onClick={handleCreateQuotation}>
                <FileText className="w-4 h-4 mr-2 text-blue-500" />
                Create Quotation
            </Button>
            
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <div title={record?.status === 'converted' ? 'Already converted to a project' : record?.status !== 'qualified' ? 'Enquiry must be Qualified before starting a project' : ''}>
                        <Button
                            variant="default"
                            size="sm"
                            disabled={record?.status !== 'qualified'}
                            className="bg-primary hover:bg-primary/90 disabled:opacity-50"
                        >
                            <LayoutGrid className="w-4 h-4 mr-2" />
                            {record?.status === 'converted' ? 'Converted to Project' : 'Start Project'}
                        </Button>
                    </div>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Project Kick-off</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to convert this enquiry into an active project? 
                            This will update the enquiry status and create a new project record.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={handleConfirmProject} disabled={projectLoading}>
                            {projectLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Confirm & Start
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const EnquiryShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto flex flex-col gap-6">
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/enquiries">Enquiries</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>{record.reference}</Breadcrumb.PageItem>
            </Breadcrumb>

            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 bg-muted/30 dark:bg-slate-900/40 border border-border/60 dark:border-slate-800/60 rounded-2xl p-6 shadow-[0_2px_8px_-1px_rgba(0,0,0,0.02)]">
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <h2 className="text-3xl font-black tracking-tight text-foreground dark:text-slate-100">{record.reference}</h2>
                        <StatusBadge status={record.status} />
                    </div>
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground flex items-center gap-2">
                        Received on <DateField source="created_at" className="font-semibold text-foreground dark:text-slate-300" /> via <Badge variant="secondary" className="bg-primary/10 text-primary border-none">{record.source || 'Direct'}</Badge>
                    </p>
                </div>
                
                {/* Team Members isolated rounded layout */}
                <div className="flex flex-col gap-2 shrink-0 w-full md:w-fit min-w-[260px]">
                    <div className="p-3 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl flex items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                            <UserPlus className="w-4 h-4" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Sales Representative</span>
                            <span className="text-xs font-semibold text-foreground dark:text-slate-300">
                                <ReferenceField source="assigned_to" reference="sales" link="show">
                                    <AssignedUserName />
                                </ReferenceField>
                            </span>
                        </div>
                    </div>
                    {record.ops_head_id && (
                        <div className="p-3 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl flex items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                <UserPlus className="w-4 h-4" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Ops Head</span>
                                <span className="text-xs font-semibold text-foreground dark:text-slate-300">
                                    <ReferenceField source="ops_head_id" reference="sales" link="show">
                                        <AssignedUserName />
                                    </ReferenceField>
                                </span>
                            </div>
                        </div>
                    )}
                    {record.ops_team_member_id && (
                        <div className="p-3 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl flex items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                <UserPlus className="w-4 h-4" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Ops Team Member</span>
                                <span className="text-xs font-semibold text-foreground dark:text-slate-300">
                                    <ReferenceField source="ops_team_member_id" reference="sales" link="show">
                                        <AssignedUserName />
                                    </ReferenceField>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Disqualification / Lost Reason */}
            {record.status === 'disqualified' && (
                <Card className="border-red-200 bg-red-50/30 rounded-2xl">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xs font-bold text-red-600 uppercase flex items-center gap-2">
                            <LayoutGrid className="w-3 h-3 rotate-45" />
                            Disqualification Reason
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-red-700 italic">
                            "{record.lost_reason || "No reason specified."}"
                        </p>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Details */}
                <Card className="lg:col-span-2 rounded-2xl overflow-hidden border-border dark:border-slate-800 shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50 dark:bg-slate-900/30">
                        <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <LayoutGrid className="w-4 h-4 text-primary" />
                            Enquiry Overview
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 flex flex-col gap-6 bg-card dark:bg-slate-950/10">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Customer / Company isolated curved box */}
                            <div className="p-4 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Customer / Company</span>
                                <ReferenceField source="company_id" reference="companies" link="show">
                                    <TextField source="name" className="text-base font-bold text-foreground dark:text-slate-100 block hover:underline" />
                                </ReferenceField>
                                <ReferenceField source="contact_id" reference="contacts" link="show">
                                    <div className="flex items-center gap-1.5 text-xs text-primary hover:underline font-semibold mt-0.5">
                                        <TextField source="first_name" /> <TextField source="last_name" />
                                    </div>
                                </ReferenceField>
                            </div>

                            {/* Budget Estimate box removed (E-3 / V-9: budget dropped per client request) */}

                            {/* Timeline isolated curved box */}
                            <div className="p-4 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl flex flex-col gap-1.5 justify-center shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Timeline</span>
                                <div className="text-base font-semibold text-foreground dark:text-slate-100 mt-1">
                                    {record.requirements?.timeline || 'Not Specified'}
                                </div>
                            </div>
                        </div>

                        {/* Special Notes isolated curved box */}
                        <div className="p-4 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Special Notes & Requirements</span>
                            <div className="text-sm text-foreground dark:text-slate-300 leading-relaxed whitespace-pre-wrap min-h-[90px] mt-1">
                                <TextField source="requirements.special_notes" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Qualification Score card removed (V-8: Initial Score dropped per client request) */}
            </div>

            {/* Technical Specifications */}
            {record.requirements && Object.keys(record.requirements).filter(k => !['budget_min', 'budget_max', 'estimated_budget', 'timeline', 'special_notes'].includes(k)).length > 0 && (
                <Card className="overflow-hidden border-border dark:border-slate-800 rounded-2xl shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50 dark:bg-slate-900/30">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <LayoutGrid className="w-4 h-4 text-primary" />
                            Technical Specifications
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 bg-card dark:bg-slate-950/10">
                         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(record.requirements)
                                .filter(([key]) => !['budget_min', 'budget_max', 'estimated_budget', 'timeline', 'special_notes'].includes(key))
                                .map(([key, val]: [string, any]) => (
                                <div key={key} className="p-4 bg-muted/30 dark:bg-slate-900/60 border border-border/60 dark:border-slate-800/60 rounded-xl shadow-sm transition-all hover:border-primary/40">
                                    <span className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">{key.replace(/_/g, ' ')}</span>
                                    <span className="text-sm font-semibold text-foreground dark:text-slate-300">{String(val)}</span>
                                </div>
                            ))}
                         </div>
                    </CardContent>
                </Card>
            )}

            {/* Attachments */}
            {Array.isArray(record.attachments) && record.attachments.length > 0 && (
                <EnquiryAttachments attachments={record.attachments} />
            )}

            {/* Activity Timeline */}
            <div className="h-[600px]">
                <ActivityTimeline resource="enquiries" recordId={record.id} />
            </div>
        </div>
    );
};

export const EnquiryShow = () => {
    return (
        <Show disableBreadcrumb actions={<EnquiryShowActions />}>
            <EnquiryShowContent />
        </Show>
    );
};
